
<template>
    <div class="container-fluid job-container">
        <div class="row">
            <div class="col-12 col-md-6">
                <div
                    class="card job-details-card"
                    v-if="!loading && job"
                >
                    <div class="card-body">
                        <h4>{{ job.name }}</h4>
                        <p>
                            <strong>Current Status:</strong>
                            <span>{{ job.status | capitalize }}</span>
                        </p>
                        <p>
                            <strong>Job Type:</strong>
                            <span v-if="job.is_call_out">Call Out</span>
                            <span v-else>Scheduled</span>
                        </p>
                        <p v-if="job.po_number">
                            <strong>P.O. Number:</strong>
                            <span>{{ job.po_number }}</span>
                        </p>
                        <p>
                            <strong>First name:</strong>
                            <span>{{ job.first_name }}</span>
                        </p>
                        <p>
                            <strong>Last name:</strong>
                            <span>{{ job.last_name }}</span>
                        </p>
                        <div class="job-important">
                            <strong>Important Info</strong>
                            {{ job.important_info || 'No important info' }}
                        </div>
                        <div class="job-decription">
                            <strong>Description:</strong>
                            {{ job.description || 'No description' }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 col-md-6">
                <div
                    class="card job-customer-card"
                    v-if="!loading && job.customer"
                >
                    <div class="card-body">
                        <h5>Customer</h5>
                        <a
                            href="#"
                            name="customer"
                        />
                        <p>Company: {{ job.customer.company_name }}</p>
                        <router-link
                            :to="{ name:'office.customers.show', params: { id: job.customer_id } }"
                            class="btn btn-info"
                        >
                            View Customer
                        </router-link>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12 col-md-6">
                <div
                    class="card job-times-card"
                    v-if="!loading && job.time_slots"
                >
                    <div class="card-body">
                        <h5>Times</h5>
                        <table
                            class="job-times-table table"
                            v-if="job.time_slots"
                        >
                            <thead>
                                <tr>
                                    <th>Start</th>
                                    <th>End</th>
                                    <th>Engineer</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(timeSlot, index) of job.time_slots"
                                    :key="index"
                                >
                                    <td>
                                        {{ timeSlot.start.format('ddd Do MMM') }}
                                    </td>
                                    <td>
                                        {{ timeSlot.end.format('ddd Do MMM') }}
                                    </td>
                                    <td>
                                        {{ timeSlot.user.name }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p v-if="!job.time_slots">
                            No times assigned
                        </p>
                    </div>
                </div>

                <div
                    class="card"
                    v-if="!loading && job.customer_id"
                >
                    <div class="card-body">
                        <h5>Notes</h5>
                        <comments :lock-site="true" />
                    </div>
                </div>
            </div>

            <div class="col-12 col-md-6">
                <div
                    class="card job-site-card"
                    v-if="!loading && job.site"
                >
                    <div class="card-body">
                        <h5>Site</h5>
                        <a
                            href="#"
                            name="site"
                        />
                        <div
                            class="job-map"
                            id="job-map"
                            v-if="showMap"
                        />
                        <h6>{{ job.site.name }}</h6>
                        <ul class="address-list">
                            <li
                                v-for="(line, index) of address"
                                :key="index"
                            >
                                {{ line }}
                            </li>
                        </ul>
                        <a
                            v-if="directionsUrl"
                            :href="directionsUrl"
                            class="btn btn-outline-secondary"
                            target="_blank"
                        >
                            <i class="fa fa-location-arrow" /> View Map
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div
                    class="card forms-times-card"
                    v-if="!loading && job.forms"
                >
                    <div class="card-body">
                        <h5>Forms</h5>
                        <table class="job-forms-list table">
                            <tr
                                v-for="(form, index) of job.forms"
                                :key="index"
                                class="job-forms-item"
                            >
                                <td>
                                    <router-link :to="{name: 'office.forms.edit', params: {id: form.id}}">
                                        {{ form.name }}
                                    </router-link>
                                </td>
                                <td class="text-right table-icons">
                                    <router-link
                                        :to="{name: 'office.forms.edit', params: { id: form.id }}"
                                        title="Edit Form"
                                    >
                                        <i class="fa fa-pencil-alt" />
                                    </router-link>
                                    <a
                                        v-if="form.id"
                                        :href="'/api/forms/' + form.id + '/stream'"
                                        title="Print Preview"
                                    >
                                        <i class="fa fa-print" />
                                    </a>

                                    <a
                                        v-if="form.id"
                                        :href="'/api/forms/' + form.id + '/download'"
                                        target="_blank"
                                        title="Download PDF"
                                    >
                                        <i class="fa fa-file" />
                                    </a>
                                    <router-link
                                        v-if="form.id"
                                        :to="{name: 'office.emails.new', params: {customer_id: form.customer_id}, query: {form_id: form.id}}"
                                        title="Send to Customer"
                                    >
                                        <i class="fa fa-envelope" />
                                    </router-link>
                                </td>
                            </tr>
                        </table>
                        <button
                            class="btn btn-primary"
                            @click="newForm()"
                        >
                            <i class="fa fa-plus" /> Add Form
                        </button>
                    </div>
                </div>

                <div
                    class="card"
                    v-if="job.id"
                >
                    <div class="card-body">
                        <h5>Attachments</h5>
                        <attachments
                            v-if="job.id"
                            type="App\Job"
                            :id="job.id"
                            :view-only="true"
                        />
                    </div>
                </div>

                <div
                    class="card"
                    v-if="job.invoiced_at || job.invoiced_by"
                >
                    <div class="card-body">
                        <h5>Invoiced</h5>
                        <div class="row">
                            <div
                                class="col-12 col-md-3"
                                v-if="job.invoiced_at"
                            >
                                <label for="">Invoiced On:</label>
                                <span>{{ job.invoiced_at.format('L') }}</span>
                            </div>
                            <div
                                class="col-12 col-md-3"
                                v-if="job.invoiced_by"
                            >
                                <label for="">Invoiced By:</label>
                                <span>{{ job.invoiced_by.name }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card">
                    <div class="card-body">
                        <router-link
                            :to="{ name: 'office.jobs.list' }"
                            class="btn btn-outline-secondary"
                        >
                            <i class="fa fa-search" /> Search Jobs
                        </router-link>
                        <router-link
                            :to="{ name: 'office.jobs.edit', params: { id: job.id } }"
                            class="btn btn-info"
                        >
                            <i class="fa fa-pencil-alt" /> Edit Job
                        </router-link>
                    </div>
                </div>
            </div>
        </div>

        <span v-if="loading">Loading
            <i class="fa fa-spin fa-spinner" />
        </span>
        <new-form-modal
            ref="newFormModal"
            :params="{customer_id: job.customer_id, job_id: job.id}"
        />
    </div>
</template>

<script>
import JobService from "../../../services/JobService";
import AuthService from "../../../services/AuthService";
import NewFormModal from "../../forms/NewFormModal.vue";
import UsesComments from '../../../mixins/UsesComments';
import Attachments from '../../AttachmentsComponent';

export default {
    components: {
        'new-form-modal': NewFormModal,
        Attachments,
    },
    mixins: [UsesComments],
    props: {
        id: {
            type: [String,Number],
            required: true,
        }
    },
    data: function () {
        return {
            job: {},
            loading: false,
            map: null,
            user: {},
            now: window.moment(),
            timer: null,
            token: null,
            showMap: true,
        };
    },

    computed: {
        address: function () {
            const address = [];
            const keys = [
                "address_line_1",
                "address_line_2",
                "address_line_3",
                "town",
                "region",
                "postcode",
                "country"
            ];
            if (this.job.site && this.job.site.address) {
                const a = this.job.site.address;
                for (let k of keys) {
                    if (a[k]) {
                        address.push(a[k]);
                    }
                }
            }
            return address;
        },

        directionsUrl: function () {
            if (!this.job.site.address.lat || !this.job.site.address.lng) {
                return null;
            }
            return encodeURI(
                [
                    "https://www.google.com/maps/dir/?api=1",
                    "destination=" +
                        this.job.site.address.lat +
                        "," +
                        this.job.site.address.lng,
                    "travelmode=driving"
                ].join("&")
            );
        }
    },

    watch: {
        job () {
            if (this.job) {
                this.setCommentsParams({
                    customerId: this.job.customer_id,
                    siteId: this.job.site_id,
                });
            }
        },
    },

    mounted: function () {
        AuthService.getUser().then(user => {
            this.user = user;
        });
        this.loadJob();
        this.timer = setInterval(() => {
            this.now = window.moment();
        }, 100);
    },

    unmounted: function () {
        clearInterval(this.timer);
    },

    methods: {
        loadJob: function () {
            this.loading = true;
            JobService.get(this.id).then(
                res => {
                    this.job = res.data;
                    this.loading = false;
                    this.initMap();
                    window.metaTitle('Job | ' + this.job.name);
                },
                err => {
                    alert("Error: Failed to load job");
                }
            );
        },

        initMap: function () {
            if (!this.job.site || !this.job.site.address) {
                return;
            }
            if(!parseFloat(this.job.site.address.lat) || !parseFloat(this.job.site.address.lng)) {
                this.showMap = false;
                return;
            }
            const site = {
                lat: this.job.site.address.lat,
                lng: this.job.site.address.lng
            };
            setTimeout(() => {
                this.map = new window.google.maps.Map(document.getElementById("job-map"), {
                    zoom: 16,
                    center: site
                });
                new window.google.maps.Marker({
                    position: site,
                    map: this.map
                });
            }, 500);
        },

        newForm: function () {
            this.$refs.newFormModal.show();
        },
    }
};
</script>




// WEBPACK FOOTER //
// resources/assets/js/components/office/jobs/ShowComponent.vue